import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Row,
  Col,
  Table,
  Spin,
  notification,
  Icon,
  Card,
  Popconfirm,
} from "antd";
import moment from "moment";

import { Container, Footer, Title } from "../form/styles";
import BreadCrumbs from "../../../components/utils/breadCrumbs";
import ButtonStandard from "../../../components/utils/button";
import {
  getColumnSearchProps,
  getColumnDateSearchProps,
  getColumnNumberSearchProps,
  getColumnDateWithBetweenSearchProps,
} from "../../../utils/tableUtils";
import { numberMask } from "../../../services/helpersMethodsService";
import { callAnimalIndex } from "../../../services/animalService";
import {
  showBoitelContract,
  saveBoitelContractAnimals,
} from "../../../services/boitelContractService";
import {
  getAnimalSearchString,
  getAnimalSortField,
} from "./utils/getAnimalSearchString";
import RelateDrawer from "./components/RelateDrawer";
import { logEventInfo } from "../../../utils/logger";

const dateFormat = "DD/MM/YYYY";

const BoitelContractSelectAnimals = () => {
  const history = useHistory();
  const { id: contractId } = useParams();
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const [loadingContract, setLoadingContract] = useState(true);
  const [loadingAnimals, setLoadingAnimals] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);
  const [herds, setHerds] = useState([]);
  const [animalData, setAnimalData] = useState({
    content: [],
    totalElements: 0,
    number: 0,
    size: 10,
  });
  const [tableState, setTableState] = useState({
    page: 1,
    size: 10,
    sorter: null,
    filters: {},
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [relatedAnimals, setRelatedAnimals] = useState([]);
  const [relateDrawerVisible, setRelateDrawerVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const t = translation?.boitelContractsPage?.selectAnimals || {};
  const animalTranslation = translation?.animal?.columns || {};

  const totalSelectedCount = (relatedAnimals || []).reduce(
    (acc, g) => acc + (g.animals?.length || 0),
    0,
  );

  const onSelectRowTable = {
    type: "checkbox",
    selectedRowKeys: selectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: (relatedAnimals || [])
        .map((g) => g.animals?.map((a) => a.animalId))
        .flat()
        .includes(record.id),
    }),
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      if (selected) {
        setSelectedRowKeys((old) => [...old, record.id]);
        setSelectedRows((old) => [...old, record]);
      } else {
        setSelectedRowKeys((old) => old.filter((r) => r !== record.id));
        setSelectedRows((old) => old.filter((r) => r.id !== record.id));
      }
    },
    onSelectAll: (record, selected, selectedRows, nativeEvent) => {
      // Get just the ids
      const keys = selectedRows.map((sr) => sr.id);
      // Get all the selectedAnimalsKeys unless the unselected
      const lastSelectedAnimalsKeys = selectedRowKeys.filter(
        (sAK) => !keys.includes(sAK),
      );
      if (record) {
        setSelectedRowKeys((old) => [...old, ...keys]);
        setSelectedRows((old) => [...old, ...selectedRows]);
      } else {
        setSelectedRowKeys(lastSelectedAnimalsKeys);
        setSelectedRows((old) => old.filter((r) => !keys.includes(r.id)));
      }
    },
  };

  const fetchContract = useCallback(async () => {
    if (!contractId) return;
    setLoadingContract(true);
    try {
      const {
        data: { results },
      } = await showBoitelContract({
        groupId,
        farmId,
        id: contractId,
      });
      setContract(results || null);
      setHerds(
        (results?.herds || []).map((h) => ({
          ...h,
          breedId: h.breedId || h.breed_id,
          breedName: h.breedName || h.breed_name,
          amountAnimals: h.amountAnimals ?? h.amount_animals,
        })),
      );
      setLoadingContract(false);
    } catch (err) {
      setLoadingContract(false);
      notification.error({
        message: translation?.error?.error || "Erro",
        description:
          translation?.boitelContractsPage?.form?.errorLoading ||
          "Erro ao carregar contrato.",
      });
      history.push("/admin/boitel/contracts");
    }
  }, [contractId, groupId, farmId, history, translation]);

  const fetchAnimals = useCallback(
    async (page, size, sorter, filters) => {
      setLoadingAnimals(true);
      try {
        const search = getAnimalSearchString(filters);
        const sortField = getAnimalSortField(sorter);
        const sorterPayload =
          sortField && sorter?.order
            ? { field: sortField, order: sorter.order }
            : null;

        const {
          data: { results },
        } = await callAnimalIndex({
          groupId,
          farmId,
          page: page || 1,
          size: size || 10,
          sorter: sorterPayload,
          filters: search,
        });

        const content = results?.content ?? results ?? [];
        const total = results?.totalElements ?? content.length;
        const num = results?.number ?? page - 1;
        const sz = results?.size ?? size;

        setAnimalData({
          content,
          totalElements: total,
          number: num,
          size: sz,
        });
      } catch (err) {
        notification.error({
          message: translation?.error?.error || "Erro",
          description: t.errorLoadingAnimals || "Erro ao carregar animais.",
        });
        setAnimalData((prev) => ({ ...prev, content: [] }));
      } finally {
        setLoadingAnimals(false);
      }
    },
    [groupId, farmId, translation, t.errorLoadingAnimals],
  );

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setTableState({
      page: pagination.current,
      size: pagination.pageSize,
      sorter: sorter?.columnKey ? sorter : null,
      filters: filters || {},
    });
  }, []);

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const getColumnTextSearchProps = useCallback(
    (dataIndex) => getColumnSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const getColumnDateFilterProps = useCallback(
    (dataIndex, useBetween = false) =>
      useBetween
        ? getColumnDateWithBetweenSearchProps(
            dataIndex,
            handleReset,
            handleSearch,
          )
        : getColumnDateSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const getColumnNumberFilterProps = useCallback(
    (dataIndex) =>
      getColumnNumberSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const herdLabel = useCallback(
    (herd) => {
      const breed = herd.breedName || herd.breed_name || "";
      const genderKey = herd.gender;
      const gender =
        genderKey &&
        (translation?.boitelContractsPage?.table?.column?.animalGenderOptions?.[
          genderKey
        ] ||
          genderKey);
      return `${breed} / ${gender || ""}`.trim() || herd.id;
    },
    [translation],
  );

  const getHerdNameForAnimal = useCallback(
    (animalId) => {
      for (const g of relatedAnimals) {
        const found = (g.animals || []).find((a) => a.animalId === animalId);
        if (found) return g.herdLabel;
      }
      return null;
    },
    [relatedAnimals],
  );

  const relatedCountByHerdId = React.useMemo(() => {
    const map = {};
    (relatedAnimals || []).forEach((g) => {
      map[g.herdId] = (map[g.herdId] || 0) + (g.animals?.length || 0);
    });
    return map;
  }, [relatedAnimals]);

  const handleRelateSave = useCallback(
    (herdId, singleDate, perAnimalDates) => {
      const herd = herds.find((h) => h.id === herdId);
      if (!herd) return;

      const herdLabelStr = herdLabel(herd);
      const animals = selectedRows.map((animal) => ({
        animalId: animal.id,
        animal,
        animalEntryDate:
          singleDate != null
            ? moment(singleDate).format("YYYY-MM-DD")
            : perAnimalDates?.[animal.id] || null,
      }));

      setRelatedAnimals((prev) => [
        ...prev,
        {
          herdId,
          herdLabel: herdLabelStr,
          animals,
          key: `${herdId}-${Date.now()}`,
        },
      ]);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setRelateDrawerVisible(false);
      setEditingGroup(null);
    },
    [herds, selectedRows, herdLabel],
  );

  const handleSaveEdit = useCallback((herdId, singleDate, perAnimalDates) => {
    setRelatedAnimals((prev) =>
      prev.map((g) => {
        if (g.herdId !== herdId) return g;
        const animals = (g.animals || []).map((a) => ({
          ...a,
          animalEntryDate:
            singleDate != null
              ? moment(singleDate).format("YYYY-MM-DD")
              : (perAnimalDates?.[a.animalId] ?? a.animalEntryDate),
        }));
        return { ...g, animals };
      }),
    );
    setRelateDrawerVisible(false);
    setEditingGroup(null);
  }, []);

  const handleDeleteRelated = useCallback((key) => {
    setRelatedAnimals((prev) => prev.filter((g) => g.key !== key));
  }, []);

  const handleEditRelated = useCallback((group) => {
    setEditingGroup(group);
    setRelateDrawerVisible(true);
  }, []);

  const handleRelateDrawerClose = useCallback(() => {
    setRelateDrawerVisible(false);
    setEditingGroup(null);
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  const handleSaveContractSelectAnimal = useCallback(async () => {
    if (totalSelectedCount === 0) {
      notification.warning({
        message: t.noAnimalsToSave || "Nenhum animal relacionado para salvar.",
      });
      return;
    }

    const body = {
      herdAnimals: (relatedAnimals || []).map((g) => ({
        herdId: g.herdId,
        animals: (g.animals || []).map((a) => ({
          animalId: a.animalId,
          animalEntryDate: a.animalEntryDate,
        })),
      })),
    };

    logEventInfo("handleSaveContractSelectAnimal - body", body);
    setSaving(true);
    try {
      await saveBoitelContractAnimals({
        groupId,
        farmId,
        id: contractId,
        body,
      });
      notification.success({
        message: t.saveSuccess || "Animais do contrato salvos com sucesso.",
      });
      history.push("/admin/boitel/contracts");
    } catch (err) {
      notification.error({
        message: translation?.error?.error || "Erro",
        description: t.saveError || "Erro ao salvar animais do contrato.",
      });
    } finally {
      setSaving(false);
    }
  }, [
    totalSelectedCount,
    relatedAnimals,
    t.noAnimalsToSave,
    t.saveSuccess,
    t.saveError,
    groupId,
    farmId,
    contractId,
    history,
    translation,
  ]);

  const handleCancel = useCallback(() => {
    history.push("/admin/boitel/contracts");
  }, [history]);

  const identificationColumn = {
    title: animalTranslation.handlingNumber || "Identificação",
    dataIndex: "handlingNumber",
    key: "handlingNumber",
    align: "left",
    width: 160,
    sorter: true,
    sortOrder:
      tableState.sorter?.columnKey === "handlingNumber"
        ? tableState.sorter.order
        : null,
    ...getColumnTextSearchProps("handlingNumber"),
    render: (text, record) =>
      record.handlingNumber ||
      record.tagId ||
      record.sisbov ||
      record.nickname ||
      "-",
  };

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  useEffect(() => {
    if (!loadingContract) {
      fetchAnimals(
        tableState.page,
        tableState.size,
        tableState.sorter,
        tableState.filters,
      );
    }
  }, [
    loadingContract,
    tableState.page,
    tableState.size,
    tableState.sorter,
    tableState.filters,
    fetchAnimals,
  ]);

  return (
    <Container>
      <Spin spinning={loadingContract}>
        <Row type="flex" justify="space-between" align="middle">
          <Col span={12}>
            <Title>
              {t.title || "Selecionar Animais do Contrato"} :{" "}
              {contract?.identificationContract}
            </Title>
          </Col>
          <Col span={12} align="right">
            <BreadCrumbs>
              <span
                onClick={() => history.push("/admin/boitel/contracts")}
                style={{ cursor: "pointer" }}
              >
                <span className="pageTreeInative">Decisões</span>
              </span>
              <span className="pageTreeActive">
                {translation?.boitelContractsPage?.title ||
                  "Boitel - Contratos"}
              </span>
            </BreadCrumbs>
          </Col>
        </Row>

        <div className="formContent">
          <Row type="flex" justify="end" style={{ marginBottom: 16 }}>
            <ButtonStandard
              buttonType="addNew"
              type="button"
              onClick={() => setRelateDrawerVisible(true)}
              disabled={selectedRowKeys.length === 0}
            >
              <Icon type="link" /> {t.relateButton || "Relacionar"}
            </ButtonStandard>
          </Row>

          <Row type="flex" gutter={16}>
            <Col span={16}>
              <Table
                rowKey="id"
                loading={loadingAnimals}
                rowSelection={onSelectRowTable}
                dataSource={animalData.content}
                pagination={{
                  total: animalData.totalElements,
                  current: tableState.page,
                  pageSize: tableState.size,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "30", "100", "200"],
                  hideOnSinglePage: animalData.totalElements <= 10,
                }}
                onChange={handleTableChange}
                scroll={{ x: true }}
                footer={() => (
                  <span>
                    <strong>
                      {t.footerTotalSelected ||
                        "Animais selecionados para o contrato:"}{" "}
                    </strong>
                    {selectedRowKeys.length}
                  </span>
                )}
              >
                <Table.Column {...identificationColumn} />
                <Table.Column
                  title={animalTranslation.tagId || "Id. Eletrônico"}
                  dataIndex="tagId"
                  key="tagId"
                  width={160}
                  align="left"
                  {...getColumnTextSearchProps("tagId")}
                />
                <Table.Column
                  title={animalTranslation.sisbov || "Sisbov"}
                  dataIndex="sisbov"
                  key="sisbov"
                  width={160}
                  align="left"
                  {...getColumnTextSearchProps("sisbov")}
                  render={(text) => (text && text > 0 ? text : "")}
                />
                <Table.Column
                  title={animalTranslation.picket || "Piquete"}
                  dataIndex="picketName"
                  key="picketName"
                  width={140}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "picketName"
                      ? tableState.sorter.order
                      : null
                  }
                  {...getColumnTextSearchProps("picketName")}
                />
                <Table.Column
                  title={animalTranslation.lot || "Lote"}
                  dataIndex="lotName"
                  key="lotName"
                  width={140}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "lotName"
                      ? tableState.sorter.order
                      : null
                  }
                  {...getColumnTextSearchProps("lotName")}
                />
                <Table.Column
                  title={t.herdsColumn || "Conjunto"}
                  dataIndex="herds"
                  key="herds"
                  width={180}
                  align="left"
                  render={(_, record) => getHerdNameForAnimal(record.id) || "-"}
                />
                <Table.Column
                  title={animalTranslation.breed || "Raça"}
                  dataIndex="breedName"
                  key="breedName"
                  width={120}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "breedName"
                      ? tableState.sorter.order
                      : null
                  }
                  {...getColumnTextSearchProps("breedName")}
                />
                <Table.Column
                  title={animalTranslation.gender || "Sexo"}
                  dataIndex="gender"
                  key="gender"
                  width={100}
                  align="left"
                  filteredValue={tableState.filters?.gender || null}
                  filters={[
                    {
                      text: translation?.animal?.details?.male || "Macho",
                      value: "M",
                    },
                    {
                      text: translation?.animal?.details?.female || "Fêmea",
                      value: "F",
                    },
                  ]}
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "gender"
                      ? tableState.sorter.order
                      : null
                  }
                  render={(g) =>
                    g === "M"
                      ? translation?.animal?.details?.male || "Macho"
                      : translation?.animal?.details?.female || "Fêmea"
                  }
                />
                <Table.Column
                  title={animalTranslation.birthdayMonths || "Idade"}
                  dataIndex="birthdayMonth"
                  key="birthdayMonth"
                  width={80}
                  align="left"
                  render={(text, record) =>
                    record.birthdayMonth != null
                      ? `${record.birthdayMonth}m`
                      : "-"
                  }
                />
                <Table.Column
                  title={animalTranslation.acquisitionDate || "Data de Entrada"}
                  dataIndex="acquisitionDate"
                  key="acquisitionDate"
                  width={120}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "acquisitionDate"
                      ? tableState.sorter.order
                      : null
                  }
                  render={(text) =>
                    text ? moment(text).format(dateFormat) : "-"
                  }
                  {...getColumnDateFilterProps("acquisitionDate", true)}
                />
                <Table.Column
                  title={animalTranslation.supplierName || "Fornecedor"}
                  dataIndex="supplierName"
                  key="supplierName"
                  width={140}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "supplierName"
                      ? tableState.sorter.order
                      : null
                  }
                  render={(text) => text || "-"}
                  {...getColumnTextSearchProps("supplierName")}
                />
                <Table.Column
                  title={animalTranslation.currentWeight || "Últ. Peso"}
                  dataIndex="lastDailyWeight"
                  key="lastDailyWeight"
                  width={120}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "lastDailyWeight"
                      ? tableState.sorter.order
                      : null
                  }
                  {...getColumnNumberFilterProps("lastDailyWeight")}
                  render={(text) =>
                    text != null ? `${numberMask(text, false)}kg` : "-"
                  }
                />
                <Table.Column
                  title={animalTranslation.gdp || "GMD"}
                  dataIndex="lastDailyWeightGain"
                  key="lastDailyWeightGain"
                  width={120}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "lastDailyWeightGain"
                      ? tableState.sorter.order
                      : null
                  }
                  {...getColumnNumberFilterProps("lastDailyWeightGain")}
                  render={(text) =>
                    text != null ? `${numberMask(text, false)} kg` : "-"
                  }
                />
                <Table.Column
                  title={
                    animalTranslation.lastDailyWeightDate || "Últ. Pesagem"
                  }
                  dataIndex="lastDailyWeightDate"
                  key="lastDailyWeightDate"
                  width={140}
                  align="left"
                  sorter
                  sortOrder={
                    tableState.sorter?.columnKey === "lastDailyWeightDate"
                      ? tableState.sorter.order
                      : null
                  }
                  {...getColumnDateFilterProps("lastDailyWeightDate", true)}
                  render={(text) =>
                    text ? moment(text).format(dateFormat) : "-"
                  }
                />
              </Table>
            </Col>
            <Col span={8}>
              {relatedAnimals.length > 0 && (
                <Row style={{ marginTop: 24 }}>
                  <label className="sectionTitle">
                    {t.summaryTitle || "Resumo por conjunto"}
                  </label>
                  <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                    {relatedAnimals.map((g, index) => (
                      <Col span={24} key={g.key}>
                        <Card
                          size="small"
                          title={
                            <span>
                              {g.herdLabel} — {g.animals?.length || 0}{" "}
                              {t.animalsLabel || "animais"}
                            </span>
                          }
                          actions={[
                            <span
                              key="edit"
                              onClick={() => handleEditRelated(g)}
                              style={{ cursor: "pointer" }}
                            >
                              <Icon type="edit" /> {t.cardEdit || "Editar"}
                            </span>,
                            <Popconfirm
                              key="delete"
                              title={
                                t.deleteConfirmTitle || "Excluir relacionamento"
                              }
                              onConfirm={() => handleDeleteRelated(g.key)}
                              okText={translation?.buttons?.confirm || "Sim"}
                              cancelText={
                                translation?.buttons?.cancel || "Cancelar"
                              }
                            >
                              <span
                                style={{ cursor: "pointer", color: "#d44c4c" }}
                              >
                                <Icon type="delete" />{" "}
                                {t.cardDelete || "Excluir"}
                              </span>
                            </Popconfirm>,
                          ]}
                        >
                          {(g.animals || []).slice(0, 5).map((a) => (
                            <div
                              key={a.animalId}
                              style={{
                                padding: "4px 0",
                                borderBottom: "1px solid #f0f0f0",
                                fontSize: 12,
                              }}
                            >
                              {a.animal?.handlingNumber ||
                                a.animal?.tagId ||
                                a.animal?.sisbov ||
                                a.animalId}
                              {a.animalEntryDate && (
                                <span style={{ color: "#888", marginLeft: 8 }}>
                                  —{" "}
                                  {moment(a.animalEntryDate).format(dateFormat)}
                                </span>
                              )}
                            </div>
                          ))}
                          {(g.animals || []).length > 5 && (
                            <div
                              style={{
                                padding: "4px 0",
                                fontSize: 12,
                                color: "#888",
                              }}
                            >
                              ...
                            </div>
                          )}
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Row>
              )}
            </Col>
          </Row>
        </div>

        <Footer>
          <Row type="flex">
            <Col span={24} className="buttonsDiv">
              <ButtonStandard
                type="button"
                buttonType="type7"
                onClick={handleCancel}
              >
                {translation?.buttons?.cancel || "Cancelar"}
              </ButtonStandard>
              <ButtonStandard
                type="button"
                buttonType="type6"
                onClick={handleSaveContractSelectAnimal}
                disabled={saving || totalSelectedCount === 0}
              >
                {translation?.buttons?.save || "Salvar"}
              </ButtonStandard>
            </Col>
          </Row>
        </Footer>
      </Spin>

      <RelateDrawer
        visible={relateDrawerVisible}
        onClose={handleRelateDrawerClose}
        herds={herds}
        selectedAnimals={
          editingGroup
            ? editingGroup.animals?.map((a) => ({
                ...a.animal,
                id: a.animalId,
              }))
            : selectedRows
        }
        relatedCountByHerdId={relatedCountByHerdId}
        onSave={handleRelateSave}
        onSaveEdit={handleSaveEdit}
        editGroup={editingGroup}
        translation={translation}
      />
    </Container>
  );
};

export default BoitelContractSelectAnimals;
